
import { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Settings, defaultSettings } from '@/types/settings';
import { fetchSettingsFromDB } from '@/utils/settingsUtils';

export const useRealtimeSettings = () => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isInitializedRef = useRef(false);
  const channelRef = useRef<any>(null);
  const cacheTimeRef = useRef(0);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fonction de chargement des paramètres avec cache optimisé
  const loadSettings = async (force = false) => {
    // Skip if loading is already in progress
    if (loading && !force) return;
    
    // Use cache if it's recent (within 2 minutes) and not forced refresh
    const now = Date.now();
    if (!force && isInitializedRef.current && (now - cacheTimeRef.current < 120000)) {
      return;
    }
    
    // Only show loading for initial load or forced refresh
    if (!isInitializedRef.current || force) {
      setLoading(true);
    }
    
    try {
      const data = await fetchSettingsFromDB();
      if (data) {
        setSettings(data);
        cacheTimeRef.current = now;
      }
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des paramètres:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      // Keep current settings in case of error
    } finally {
      // Clear loading with a small delay to prevent flashing
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      loadingTimeoutRef.current = setTimeout(() => {
        setLoading(false);
        isInitializedRef.current = true;
      }, 100);
    }
  };

  // Memoized version of settings to prevent unnecessary rerenders
  const memoizedSettings = useMemo(() => settings, [
    settings.title,
    settings.email,
    settings.phone,
    settings.whatsappLink,
    
    settings.footerText,
    settings.logoUrl,
    settings.showVirtualAssistant,
    
    // Banner settings
    settings.bannerEnabled,
    settings.bannerText,
    settings.bannerBgColor,
    settings.bannerTextColor,
    settings.bannerLink,
    settings.bannerLinkText,
    
    // Dates de mise à jour des pages légales
    settings.cgvUpdatedAt,
    settings.mentionsLegalesUpdatedAt,
    settings.cguUpdatedAt,
    settings.privacyPolicyUpdatedAt,
  ]);

  useEffect(() => {
    // Ne pas s'abonner plusieurs fois
    if (channelRef.current) return;
    
    // Chargement initial différé pour éviter les clignotements
    const initialLoadTimeout = setTimeout(() => {
      if (!isInitializedRef.current) {
        loadSettings();
      }
    }, 200);
    
    // Setup realtime avec throttling renforcé
    const channel = supabase.channel('settings-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'settings' },
        () => {
          console.log("🔄 Settings changed, reloading with delay...");
          // Délai plus long pour éviter les mises à jour trop fréquentes
          setTimeout(() => {
            loadSettings(true);
          }, 1000);
        }
      )
      .subscribe();
      
    channelRef.current = channel;
    
    return () => {
      clearTimeout(initialLoadTimeout);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channel);
        channelRef.current = null;
      }
    };
  }, []);
  
  return { 
    settings: memoizedSettings, 
    loading, 
    error,
    refresh: () => loadSettings(true)
  };
};
